import React, { useEffect, useState } from 'react';
import { useHttpContext } from '@/common/hooks/HttpContext';
import { toasts } from '@/common/toasts';
import {
    Location,
    PaginationMeta,
    PaginationOrder,
    Practitioner,
    PractitionerCounts,
    PractitionerStatus,
} from '@/common/types';
import { resultIsError } from '@/services/HttpService';
import { PractitionersList } from './PractitionersList';
import { usePractitionersContext } from './context/hooks/PractitionerContext';

interface PractitionersListContainerProps {
    locations: Location[] | undefined;
    supportsSSO: boolean;
}

const PAGE_SIZE = 20;

export const PractitionersListContainer = (props: PractitionersListContainerProps) => {
    const { locations, supportsSSO } = props;
    const { httpService } = useHttpContext();
    const { refreshSignal } = usePractitionersContext();
    const { errorToast } = toasts;

    const [status, setStatus] = useState<PractitionerStatus>(PractitionerStatus.CURRENT);
    const [page, setPage] = useState(1);
    const [order, setOrder] = useState<PaginationOrder>('ASC');
    const [practitioners, setPractitioners] = useState<Practitioner[]>();
    const [metadata, setMetadata] = useState<PaginationMeta>();
    const [counts, setCounts] = useState<PractitionerCounts>({ current: 0, invited: 0 });

    useEffect(() => {
        (async () => {
            const res = await httpService.getPractitionerPages({ status, page, limit: PAGE_SIZE, order });
            if (resultIsError(res)) {
                errorToast('Unable to get providers');
                return;
            }
            setPractitioners(res.items);
            setMetadata(res.metadata);
        })();
    }, [status, page, order, refreshSignal]);

    useEffect(() => {
        (async () => {
            const res = await httpService.getPractitionerCounts();
            if (resultIsError(res)) {
                return;
            }
            setCounts(res);
        })();
    }, [refreshSignal]);

    const handleStatusChange = (next: PractitionerStatus) => {
        setStatus(next);
        setPage(1);
    };

    const handleToggleOrder = () => {
        setPage(1);
        setOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    };

    return (
        <PractitionersList
            practitioners={practitioners}
            locations={locations}
            supportsSSO={supportsSSO}
            status={status}
            counts={counts}
            page={page}
            order={order}
            metadata={metadata}
            onStatusChange={handleStatusChange}
            onPageChange={setPage}
            onToggleOrder={handleToggleOrder}
        />
    );
};
